import { LivenessError } from "@aws-amplify/ui-react-liveness/dist/types/components/FaceLivenessDetector/service";

import {
  ApplicantWebcamGetUploadUrl,
  postWebcamGetUrls,
} from "@api/applicants";

import { livenessText } from "@shared/components/liveness/livenessText";
import Message from "@shared/services/message";

import { dataURLtoBlob } from "./image";
import Logger from "./logger";

const LivenessErrorState = {
  CONNECTION_TIMEOUT: "CONNECTION_TIMEOUT",
  TIMEOUT: "TIMEOUT",
  RUNTIME_ERROR: "RUNTIME_ERROR",
  FRESHNESS_TIMEOUT: "FRESHNESS_TIMEOUT",
  SERVER_ERROR: "SERVER_ERROR",
  CAMERA_FRAMERATE_ERROR: "CAMERA_FRAMERATE_ERROR",
  CAMERA_ACCESS_ERROR: "CAMERA_ACCESS_ERROR",
  FACE_DISTANCE_ERROR: "FACE_DISTANCE_ERROR",
  MOBILE_LANDSCAPE_ERROR: "MOBILE_LANDSCAPE_ERROR",
  MULTIPLE_FACES_ERROR: "MULTIPLE_FACES_ERROR",
};

export const getLivenessErrorMessage = (
  livenessError: LivenessError | null,
) => {
  const text = livenessText();

  const messages = {
    [LivenessErrorState.CONNECTION_TIMEOUT]: text.connectionTimeoutMessageText,
    [LivenessErrorState.TIMEOUT]: text.timeoutMessageText,
    [LivenessErrorState.SERVER_ERROR]: text.serverMessageText,
    [LivenessErrorState.CAMERA_FRAMERATE_ERROR]:
      text.cameraMinSpecificationsMessageText,
    [LivenessErrorState.CAMERA_ACCESS_ERROR]: text.cameraNotFoundMessageText,
    [LivenessErrorState.FACE_DISTANCE_ERROR]: text.faceDistanceMessageText,
    [LivenessErrorState.MULTIPLE_FACES_ERROR]: text.multipleFacesMessageText,
  };

  return livenessError?.state
    ? messages[livenessError.state]
    : Message.getMessageByKey("applicant.liveness.failed.banner.description");
};

export const getWebcamUploadUrls = async ({
  applicantExamId,
}: {
  applicantExamId: number;
}) => {
  const res = await postWebcamGetUrls({
    applicantExamId,
  }).catch((): false => false);

  if (!res || res.code !== 200) {
    Logger.error("Failed to get webcam urls");
    throw new Error(`Failed to get webcam urls`);
  }

  return res.result;
};

export const formatWebcamFormData = ({
  uploadItem,
  timestamp,
  challengeResultId,
  imgDataUrl,
}: {
  uploadItem: ApplicantWebcamGetUploadUrl["uploadUrls"][0];
  timestamp: string;
  challengeResultId: number;
  imgDataUrl: string;
}) => {
  const { fields, otherRequiredFields } = uploadItem;

  const formData = new FormData();

  for (const [key, value] of Object.entries(fields)) {
    formData.append(key, value);
  }

  const captureDateField = "x-amz-meta-capturedat";
  const activeChallengeResultField = "x-amz-meta-activechallengeresult";

  if (otherRequiredFields.includes(captureDateField)) {
    formData.append(captureDateField, timestamp);
  }

  if (otherRequiredFields.includes(activeChallengeResultField)) {
    formData.append(activeChallengeResultField, `${challengeResultId}`);
  }

  formData.append("file", dataURLtoBlob(imgDataUrl));

  return formData;
};
